import React, { useEffect, memo } from 'react';
import { initTrackingScripts } from '../lib/tracking-scripts';
import { attachAnalyticsListeners } from '@spotify-internal/touchdown-analytics';
import Page from '../../shared/components/Page';
import { globalTracer, Span } from 'opentracing';
import { WindowSpotiData } from '../types';
import { Configuration } from '@spotify-internal/remote-config-properties';
import { getTransport } from '../lib/transport';
import { RemoteConfigProvider } from '@spotify-internal/remote-config-integration-react';

type AppProps = Omit<
  WindowSpotiData,
  'environment' | 'release' | 'useErrorReporting' | 'useInstrumentation'
>;

export type DecoratedAppProps = AppProps & {
  baseElement?: HTMLElement;
  parentSpan?: Span;
};

const App = (props: DecoratedAppProps) => {
  const {
    baseElement,
    parentSpan: indexSpan,
    user,
    language,
    pageName,
    resourceName,
    market,
    country,
    forceOneTrust,
    pageData,
    direction,
    remoteConfiguration,
    device,
  } = props;

  const tracer = globalTracer();
  const appSpan = tracer.startSpan('app', { childOf: indexSpan });

  const pageProps = {
    market,
    direction,
    user,
    pageData,
    language,
    parentSpan: appSpan,
    device,
  };

  const parsedConfig = remoteConfiguration
    ? JSON.parse(remoteConfiguration)
    : {};

  const transport = getTransport();

  useEffect(() => {
    if (!transport.isConnected()) {
      transport.connect();
    }
  }, [transport]);

  useEffect(() => {
    if (language) {
      initTrackingScripts({
        forceOneTrust,
        market,
        language,
        loggedIn: user.authenticated,
      });
    }
  }, [forceOneTrust, market, language, user.authenticated]);

  useEffect(() => {
    if (baseElement) {
      attachAnalyticsListeners({
        analyticsRoot: baseElement,
        isAuthenticated: user.authenticated ?? false,
        pageName,
        resourceName,
        language,
        countryCode: country ?? '',
        eventParams: pageData.eventParams ?? {},
      });
    }
  }, [
    baseElement,
    user.authenticated,
    pageName,
    resourceName,
    language,
    country,
    pageData.eventParams,
  ]);

  appSpan.finish();

  return (
    <RemoteConfigProvider configuration={Configuration.fromJSON(parsedConfig)}>
      <Page {...pageProps} />
    </RemoteConfigProvider>
  );
};

export default memo(App);
